import { isViewerMode } from '@vindr/utils/helpers';
import { getAuthorizationHeader } from '../../DICOMWeb';
import fetchIt from './fetchIt';
import {
  getPresentationStates,
  updatePresentationState,
  createPresentationState,
  deletePresentationState,
} from './presentationState.api';
import type { ResponseGetReports } from '@vindr/services/apis/report.api';

const name = 'httpClientService';

async function getStudyList(params = {}, headers) {
  const p = new URLSearchParams(params);
  if (!window?.config?.risURL) return;

  const data = await fetchIt.get(
    `studies/v1/studylist?offset=0&limit=100&sort_field=study_date&sort_order=1&study_date_from=1975-04-30T17%3A00%3A00.000Z&study_date_to=2123-10-27T16%3A59%3A59.999Z&${p.toString()}`,
    { headers }
  );

  return data?.json()?.data;
}

async function getStudyDetail(studyUid: string, headers) {
  const data = await fetchIt.get(
    `${isViewerMode() ? 'studies/v1/' : ''}viewer-study/${studyUid}`,
    { headers }
  );

  return data?.json();
}

async function getPatientStudyList(studyUid, headers) {
  if (!window?.config?.risURL) return;

  const study = await getStudyDetail(studyUid, headers);
  if (!study?.data?.patient_id) return [];
  const studylist = await getStudyList(
    { patient_id: study?.data?.[0]?.patient_id },
    headers
  );

  localStorage.setItem('institution_id', study?.data?.[0]?.institution_id);

  return studylist;
}

async function actionPrint(body, headers = {}) {
  if (!window?.config?.risURL) return;
  const data = await fetchIt.post('internal/printers/v1', {
    body,
    redirect: 'follow',
    headers,
  });

  return await data?.json();
}

const actionGetStudyDetail = async (params = {}, headers = {}) => {
  if (!window?.config?.risURL) return;

  const data = await fetchIt.get(`studies/v1/studylist`, {
    searchParams: { limit: 1, offset: 0, ...params },
  });

  return await data?.json();
};

const actionDiagnose = async (body, headers = {}) => {
  if (!window?.config?.risURL) return;

  const data = await fetchIt.post('cad/v1/diagnose', {
    json: body,
    redirect: 'follow',
    headers,
  });

  return await data?.json();
};

const actionCheckAIResult = async (params: any = {}, headers = {}) => {
  if (!window?.config?.risURL) return;

  const data = await fetchIt.get(`cad/v1/track/${params.trackId}`);
  const jsonData = await data?.json();
  return jsonData?.data;
};

const actionGetAIResult = async (params = {}, headers = {}) => {
  if (!window?.config?.risURL) return;

  const data = await fetchIt.get(
    `cad/v1/result?${new URLSearchParams(params).toString()}`
  );
  const jsonData = await data.json();
  return jsonData?.data;
};

const createMetadata = async (data: CreateMetadataParams, headers) => {
  const response = await fetchIt
    .extend({
      headers: {
        'Content-Type': 'application/json',
      },
      prefixUrl: DEFAULT_URL_UPLOAD,
    })
    .post('pacs/connect/attachment/bulk-meta', {
      body: JSON.stringify({
        ...data,
        timezone:
          data.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone,
        usecase: data.usecase || 'study_attachment',
      }),
      redirect: 'follow',
    });

  return response?.json();
};

const getFirstRenderedImageSrc = (displayset, callback) => {
  const imageId = displayset.images[0]?.imageId;

  if (!imageId) {
    return;
  }

  let url =
    'http' +
    imageId.split(/http/)[1] +
    (imageId.includes('/rs/') ? '/rendered?quality=75' : '');

  if (url.includes('application/dicom')) {
    url = url.replace('application/dicom', 'image/jpeg');
    url = url.replace('&transferSyntax=*', '');
    // url += '&columns=1000&frameNumber=1';
    url += '&columns=320&frameNumber=1';
  }

  fetchIt
    .get(url, {
      prefixUrl: '',
      timeout: 60000,
      retry: {
        limit: 0,
      },
      headers: {
        accept: '*/*',
      },
    })
    .blob()
    .then(blob => {
      const src = blob && URL.createObjectURL(blob);

      callback(src);
    })
    .catch(err => {
      console.error(`📕 err - 45:getSopClassHandlerModule.js \n`, err);
    });
};

const getDevicesList = async institution_ids => {
  const data = await fetchIt.get(`devices/v1`, {
    searchParams: {
      institution_ids,
      types: 4,
      limit: 100,
      offset: 0,
    },
  });

  return data?.json();
};

const getReport = async (study_instance_uid): Promise<ResponseGetReports> => {
  let data;

  if (study_instance_uid.startsWith('STUDY')) {
    // Fake study_instance_uid
    const params = study_instance_uid.split(':');
    data = fetchIt.get(`${isViewerMode() ? 'reports/v1/' : ''}order-report`, {
      searchParams: {
        accession_no: params[1],
        patient_id: params[2],
      },
    });
  } else {
    data = await fetchIt.get(
      `${isViewerMode() ? 'reports/v1/' : ''}study-report/${study_instance_uid}`
    );
  }

  return data?.json();
};

const verifyConsultationPassword = async (id, body = {}, headers = {}) => {
  const data = await fetchIt
    .extend({
      headers: {
        'Content-Type': 'application/json',
      },
      prefixUrl: window?.config?.risURL.replace('/ris', ''),
    })
    .post(`consultation/v1/meetings/authentication/${id}`, {
      json: body,
      redirect: 'follow',
      headers,
    });

  return data?.json();
};

const verifyConsultationPasswordGuest = async (id, body = {}, headers = {}) => {
  console.log('headers', headers);
  const data = await fetch(`${window?.config?.risURL.replace('/ris', '')}/consultation/v1/meetings/authentication/${id}`, {
    headers: {
      ...headers,
    },
    body: JSON.stringify(body),
    method: 'POST',
    redirect: 'follow',
  });

  if (!data.ok) {
    throw new Error((await data?.json())?.error_code);
  }

  return data?.json();
};

const checkConsultationStatus = async (id, headers = {}) => {
  const data = await fetch(`${window?.config?.risURL.replace('/ris', '')}/consultation/v1/meetings/status/${id}`, {
    headers: {
      ...headers,
    },
    method: 'GET',
    redirect: 'follow',
  });

  if (!data.ok) {
    throw new Error((await data?.json())?.error_code);
  }

  return data?.json();
};

export const registerConsultationNoteSession = async (
  id: string,
  headers = {}
) => {
  const data = await fetchIt
    .extend({
      headers: {
        'Content-Type': 'application/json',
      },
      prefixUrl: window?.config?.risURL.replace('/ris', ''),
    })
    .post(`consultation/v1/meetings/${id}/notes/session`, {
      redirect: 'follow',
      headers,
    });

  return data?.json();
};

export const destroyConsultationNoteSession = async (
  id: string,
  headers = {}
) => {
  const data = await fetchIt
    .extend({
      headers: {
        'Content-Type': 'application/json',
      },
      prefixUrl: window?.config?.risURL.replace('/ris', ''),
    })
    .delete(`consultation/v1/meetings/${id}/notes/session`, {
      redirect: 'follow',
      headers,
    });

  return data?.json();
};

export const noteConsultation = async (
  id: string,
  body: any = {},
  headers: any = {}
) => {
  const data = await fetchIt
    .extend({
      headers: {
        'Content-Type': 'application/json',
      },
      prefixUrl: window?.config?.risURL.replace('/ris', ''),
    })
    .post(`consultation/v1/meetings/${id}/notes`, {
      redirect: 'follow',
      json: body,
      headers,
    });

  return data?.json();
};

export const getConsultationNote = async (id: string) => {
  const data = await fetchIt
    .extend({
      headers: {
        'Content-Type': 'application/json',
      },
      prefixUrl: window?.config?.risURL.replace('/ris', ''),
    })
    .get(`consultation/v2/meetings/${id}/notes`);

  return data?.json();
};

export const publicAPI = {
  name,
  fetchIt,
  getAuthorizationHeader,
  getStudyList,
  getStudyDetail,
  getPatientStudyList,
  actionPrint,
  actionGetStudyDetail,
  actionDiagnose,
  actionCheckAIResult,
  actionGetAIResult,
  createMetadata,
  getFirstRenderedImageSrc,
  getDevicesList,
  getPresentationStates,
  createPresentationState,
  updatePresentationState,
  deletePresentationState,
  getReport,
  verifyConsultationPassword,
  getConsultationNote,
  registerConsultationNoteSession,
  destroyConsultationNoteSession,
  noteConsultation,
  verifyConsultationPasswordGuest,
  checkConsultationStatus,
};

export default {
  REGISTRATION: {
    name,
    altName: 'HttpClientService',
    create: ({ configuration = {} }) => {
      return publicAPI;
    },
  },
};

interface CreateMetadataParams {
  filenames: string[];
  study_instance_uid: string;
  timezone?: string;
  usecase?: string;
}

export const DEFAULT_URL_UPLOAD =
  process.env.NODE_ENV === 'development'
    ? 'https://vna-dev.vindr.ai'
    : window.location.origin;
